import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

// Hook
import { useAuth } from "../hooks/useAuth";

// Components
import { OrangeButton, YellowButton } from "../components/Buttons";
import { FormCheckbox, FormInput, FormSelect } from "../components/FormInput";
import MainView from "../components/MainView";

// Helper
import { checkAuth, handleSignUp } from "../helpers/authHelper";
import {
  ageOptions,
  countryOptions,
  ethnicityOptions,
  genderOptions,
  generateDisplayName,
  validateSignUp,
} from "../helpers/formHelper";

export default function SignUp() {
  const navigate = useNavigate();

  const { auth } = useAuth();

  const { refresh_token_expired } = checkAuth(auth.token);

  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [ethnicity, setEthnicity] = useState("");
  const [country, setCountry] = useState("");
  const [postcode, setPostcode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsData, setTermsData] = useState(false);
  const [termsCond, setTermsCond] = useState(false);

  const clearForm = () => {
    setName("");
    setGender("");
    setAgeRange("");
    setEthnicity("");
    setCountry("");
    setPostcode("");
    setDisplayName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setTermsData(false);
    setTermsCond(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let payload = {
      name: name,
      gender: gender,
      age_range: ageRange,
      ethnicity: ethnicity,
      country: country,
      postcode: postcode,
      display_name: displayName,
      email: email,
      password: password,
      conf_password: confirmPassword,
      terms_data: termsData,
      terms_cond: termsCond,
    };

    let isValid = validateSignUp(payload);

    if (isValid) {
      // Handle Sign Up
      handleSignUp(payload, navigate, clearForm);
    }
  };

  // Check Auth State and Redirect to Games Page if Authenticated
  return !refresh_token_expired ? (
    <Navigate to="/games" replace />
  ) : (
    <MainView>
      <div className="mt-5 px-[5vw] 2xl:px-[15vw]">
        <div className="mb-5">
          <form id="signupForm" noValidate onSubmit={(e) => handleSubmit(e)}>
            <div className="flex flex-col">
              <div className="flex flex-col-reverse sm:flex-row sm:justify-between">
                <div className="sm:w-[48%]">
                  <div className="mb-3 sm:w-[90%] xl:w-[80%]">
                    <FormInput
                      label="Name"
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div className="mb-3 sm:w-[90%] xl:w-[80%]">
                    <FormSelect
                      label="Gender"
                      options={genderOptions}
                      id="gender"
                      value={gender}
                      onChange={(inputValue) => setGender(inputValue)}
                    />
                  </div>

                  <div className="mb-3 sm:w-[90%] xl:w-[80%]">
                    <FormSelect
                      label="Age Range"
                      options={ageOptions}
                      id="age_range"
                      value={ageRange}
                      onChange={(inputValue) => setAgeRange(inputValue)}
                    />
                  </div>

                  <div className="mb-3 sm:w-[90%] xl:w-[80%]">
                    <FormSelect
                      label="Ethicity"
                      options={ethnicityOptions}
                      id="ethnicity"
                      value={ethnicity}
                      onChange={(inputValue) => setEthnicity(inputValue)}
                    />
                  </div>

                  <div className="mb-3 sm:w-[90%] xl:w-[80%]">
                    <FormSelect
                      label="Country"
                      options={countryOptions}
                      id="country"
                      value={country}
                      onChange={(inputValue) => setCountry(inputValue)}
                    />
                  </div>

                  <div className="mb-3 sm:w-[90%] xl:w-[80%]">
                    <FormInput
                      label="Postcode/Zipcode"
                      type="text"
                      id="postcode"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                    />
                  </div>
                </div>

                <div className="sm:w-[48%] self-stretch mb-5 sm:mb-0 relative">
                  <div className="sm:absolute sm:top-0 bg-retro-sparkle-circle h-full bg-cover bg-center p-[1vw] flex items-center justify-center">
                    <p className="font-retro-rounded text-xl xl:text-[28px] text-center xl:leading-relaxed sm:w-[80%]">
                      Retro Rehab is gamified strength and balance tool
                      developed by Good Boost as part of an industrial research
                      funded by the Small Business Research Initiative (SBRI).
                      It is allowing us to research whether interactive games
                      make a difference in the strength and coordination of
                      those who play them.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col">
                <div className="mb-4 sm:w-[85%] md:w-[75%] xl:w-[60%] flex flex-col sm:flex-row sm:items-end sm:justify-between">
                  <div className="sm:w-[64%] xl:w-[64%]">
                    <FormInput
                      label="Display Name"
                      type="text"
                      id="display_name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                    />
                  </div>
                  <div className="mt-3 sm:mt-0 sm:w-[32%] xl:w-[32%]">
                    <OrangeButton
                      type="button"
                      onClick={() => {
                        setDisplayName(generateDisplayName());
                      }}
                    >
                      Auto Generate
                    </OrangeButton>
                  </div>
                </div>

                <div className="sm:w-[60%] md:w-[48%]">
                  <div className="mb-4 sm:w-[100%] md:w-[90%] xl:w-[80%]">
                    <FormInput
                      label="Email"
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="sm:w-[60%] md:w-[48%]">
                  <div className="mb-4 sm:w-[100%] md:w-[90%] xl:w-[80%]">
                    <FormInput
                      label="Password"
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>

                <div className="sm:w-[60%] md:w-[48%]">
                  <div className="mb-4 sm:w-[100%] md:w-[90%] xl:w-[80%]">
                    <FormInput
                      label="Confirm Password"
                      type="password"
                      id="conf_password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                </div>

                <div className="">
                  <div className="mb-4 md:w-[90%] xl:w-[80%]">
                    <FormCheckbox
                      label={
                        <span>
                          I accept for my data to be shared in a research
                          capacity
                        </span>
                      }
                      id="terms_data"
                      checked={termsData}
                      onChange={(e) => setTermsData(!termsData)}
                    />
                  </div>
                </div>

                <div className="">
                  <div className="mb-4 md:w-[90%] xl:w-[80%]">
                    <FormCheckbox
                      label={
                        <span>
                          I accept the{" "}
                          <Link to="/terms" className="text-retro-blue">
                            terms and conditions
                          </Link>
                        </span>
                      }
                      id="terms_cond"
                      checked={termsCond}
                      onChange={(e) => setTermsCond(!termsCond)}
                    />
                  </div>
                </div>

                <div className="">
                  <div className="mt-10 mb-4 md:w-[40%] mx-auto">
                    <YellowButton type="submit">Sign Up</YellowButton>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </MainView>
  );
}
